// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FormFieldStyle } from "@insite/mobius/FormField";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { qtyFieldStyles } from "@sc/App/Components/Qty/QtyField";
import { createHorizontalSmallHeadingAndTextStyles } from "@sc/App/SharedStyles/smallHeadingAndTextStyles";
// import cartLineSharedStyles from "@sc/App/SharedStyles/CartLineSharedStyles";
import { color, hideInputNumberArrows, mergeWithCss, xScreen } from "@sc/XDL/Utilities";
import { css } from "styled-components";

qtyFieldStyles.wrapper = {
    css: css`
        display: flex;
        width: 100%;
        position: relative;
        height: 28px;
        max-width: 81px;
        min-width: 81px;
        border-radius: 4px;

        ${({ showActionButton }: any) =>
            showActionButton &&
            css`
                & > ${FormFieldStyle} {
                    padding-left: 22px;
                    padding-right: 22px;
                }
            `}
        @media print {
            display: none;
        }
    `,
};
qtyFieldStyles.editableQuantityTextField = {
    mergeCss: true,
    backgroundColor: "transparent",
    sizeVariant: "small",
    labelProps: {
        css: css`
            display: none;
        `,
    },
    cssOverrides: {
        formField: css`
            && input:not(.react-datetime-picker__inputGroup__input):not([name="datetime"]) {
                &,
                &:active,
                &:hover,
                &:focus {
                    outline: none;
                    box-shadow: none;
                    &:not(:disabled) {
                        box-shadow: none;
                    }
                    &:disabled {
                        border: 1px solid ${color("common.border")};
                    }
                }
            }
        `,
        formInputWrapper: css`
            &&&& {
                margin-top: 0;
            }
        `,

        inputSelect: css`
            font-size: 14px;
            line-height: 16px;
            text-align: center;
            padding: 0 !important;
            height: 28px;
            box-shadow: none !important;
            border: 1px solid ${color("common.ui4")};

            &:hover {
                border-color: ${color("common.ui8")};
            }
            &:focus {
                border-color: ${color("common.ui9")};
            }

            &:hover,
            &:focus {
                border: none;
                box-shadow: none !important;
            }
            &:disabled {
                background-color: transparent;
            }
            &&& {
                @media print {
                    display: none;
                }
            }

            ${hideInputNumberArrows}
        `,
    },
};
qtyFieldStyles.countButton = {
    variant: "tertiary",
    sizeVariant: "small",
    css: css`
        position: absolute;
        top: 0.8px;
        min-height: 26px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 26px;
        flex: 0 0 22px;
        padding: 0;
        border: 0;
        z-index: 1;
        &:hover:not(:disabled) {
            ${IconWrapper} {
                color: ${color("common.ui8")};
            }
        }
        &:focus:not(:disabled) {
            ${IconWrapper} {
                color: ${color("common.white")};
            }
        }
    `,
};
qtyFieldStyles.countIcon = {
    color: "primary",
    size: 14,
    mergeCss: true,
};

qtyFieldStyles.decreaseButton = mergeWithCss(qtyFieldStyles.countButton, {
    css: css`
        left: 0.8px;
    `,
});

qtyFieldStyles.increaseButton = mergeWithCss(qtyFieldStyles.countButton, {
    css: css`
        right: 0.8px;
    `,
});
qtyFieldStyles.printQtyLabel = {
    as: "label",
    size: 12,
    weight: "normal",
    lineHeight: "14px",
    css: css`
        margin-bottom: 4px;
        display: none;

        @media print {
            &&& {
                display: block;
                font-size: 14px;
                line-height: 16px;
            }
        }
    `,
};
qtyFieldStyles.printValueText = {
    as: "p",
    css: css`
        display: none;
        @media print {
            margin-bottom: 5px;
            display: block;
            font-size: 14px;
        }
    `,
};

qtyFieldStyles.qtyLabel = {
    as: "label",
    size: 12,
    weight: "normal",
    lineHeight: "14px",
    css: css`
        margin-bottom: 4px;
        display: none;
        ${xScreen("lte", "md")} {
            display: block;
        }
        @media print {
            display: none;
        }
    `,
};

qtyFieldStyles.questionIconStyles = {
    size: 14,
    css: css`
        opacity: 0.66;
        color: ${color("text.link")};
        &:hover {
            opacity: 1;
        }
        margin-right: 8px;
    `,
};

qtyFieldStyles.tooltipBody = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 8px;
    `,
};
qtyFieldStyles.headingAndTextStyles = {
    ...createHorizontalSmallHeadingAndTextStyles({
        heading: {
            size: 14,
            weight: "normal",
            color: "common.ui1",
            css: css`
                line-height: 22px;
            `,
        },
        text: {
            size: 14,
            weight: "normal",
            color: "common.ui1",
            css: css`
                line-height: 22px;
            `,
        },
    }),
};

qtyFieldStyles.multipleSaleQtyToolTip = {
    cssOverrides: {
        tooltipClickable: css`
            margin: 0;
            margin-top: 4px;
        `,
        tooltipContainer: css`
            min-width: 256px;
        `,
    },
};

qtyFieldStyles.qtyTooltipWrapper = {
    css: css`
        display: flex;
        column-gap: 4px;
        &&&.editableQtyTooltip {
            span[class^="TooltipClickable"] {
                margin-top: 7px;
            }
        }
        @media print {
            ${TypographyStyle} {
                font-size: 14px;
                line-height: 17px;
            }
        }
    `,
};
qtyFieldStyles.multipleSaleQtyWrapper = {
    css: css`
        display: flex;
    `,
};
